import { Link, useLocation } from "react-router";
import { useMe } from "@/api/queries";
import { Glyph } from "@/components/ui/Glyph";
import { LogoTile } from "@/components/ui/Logo";
import { BOTTOM_NAV, MAIN_NAV, isCurrent, type Destination } from "./nav";
import { useMediaQuery } from "./useMediaQuery";
import styles from "./shell.module.css";

/** Tablets get the compact rail: the icons alone, with each name as a tooltip. */
const COMPACT = "(max-width: 1100px)";

function RailItem({
  item,
  badge,
  current,
}: {
  item: Destination;
  badge?: number;
  current: boolean;
}) {
  return (
    <Link
      to={item.to}
      className={[styles.item, item.needs ? styles.needs : ""].filter(Boolean).join(" ")}
      aria-current={current ? "page" : undefined}
      title={item.label}
    >
      <span className={styles.tile}>
        <Glyph name={item.glyph} size={20} strokeWidth={1.8} />
        {badge ? (
          <span className={styles.badge}>
            {badge}
            <span className="visually-hidden"> waiting for you</span>
          </span>
        ) : null}
      </span>
      <span className={styles.label}>{item.label}</span>
    </Link>
  );
}

/**
 * The rail, set on the app's Midnight Blue frame in both themes (the screen floats beside it as
 * one panel). On a phone the tab bar takes its place.
 */
export function Rail() {
  const { pathname } = useLocation();
  const me = useMe();
  const compact = useMediaQuery(COMPACT);
  const waiting = me.data?.needs_you_count ?? 0;
  return (
    <div className={`${styles.rail} ${compact ? styles.railCompact : ""}`}>
      <nav aria-label="Main" className={styles.nav}>
        <Link to="/" aria-label="ZeaOps home" className={styles.logo}>
          <LogoTile size={compact ? 40 : 46} />
        </Link>
        {MAIN_NAV.map((item) => (
          <RailItem
            key={item.to}
            item={item}
            current={isCurrent(item, pathname)}
            badge={item.needs ? waiting : undefined}
          />
        ))}
      </nav>
      <nav aria-label="Workspace" className={`${styles.nav} ${styles.navBottom}`}>
        {BOTTOM_NAV.map((item) => (
          <RailItem key={item.to} item={item} current={isCurrent(item, pathname)} />
        ))}
      </nav>
    </div>
  );
}
